import { AppTextService } from '@fi-shared/app-text/app-text.service';
import { ObjectTypes } from '@fi-shared/types/object-type';
import { ILightWeightObject } from '@fi-shared/models/local-search.model';
import {
    IAccessRightFilterCollection,
    ILightWeightBusinessUnit,
    ILightWeightUser,
} from '@fi-shared/models/access-right-filter-collection.model';
import { getObjectTypeNameTextId } from '@fi-shared/utils';
import { IUserProfile } from '@fi-shared/profile-icon/profile-icon.component';
import { FiTaskAssignmentType } from '@fi-app/task-v2/fi-task-v2-enums';

export class FiTaskV2AssigneesHelper {
    public readonly assignedRoles: ILightWeightObject[];
    public readonly assignedBusinessUnits: ILightWeightBusinessUnit[];
    public readonly assignedUsers: ILightWeightUser[] | IUserProfile[];
    private readonly appTextService: AppTextService;
    private readonly assignmentType: FiTaskAssignmentType;

    constructor(
        appTextService: AppTextService,
        accessRightFilterCollection: IAccessRightFilterCollection,
        assignedUsers: IUserProfile[],
        assignmentType: FiTaskAssignmentType,
    ) {
        this.appTextService = appTextService;
        this.assignedRoles = accessRightFilterCollection.AccessRightCoreFilter?.Roles || [];
        this.assignedBusinessUnits = accessRightFilterCollection.AccessRightCoreFilter?.BusinessUnits || [];
        this.assignmentType = assignmentType;
        this.assignedUsers = assignedUsers;
    }

    public get assignedRolesCount(): number {
        return this.assignedRoles.length;
    }

    public get assignedBusinessUnitsCount(): number {
        return this.assignedBusinessUnits.length;
    }

    public get assignedUserCount(): number {
        return this.assignedUsers.length;
    }

    public get isMultipleRoleUnitsAssigned(): boolean {
        return this.assignedRolesCount > 1 || this.assignedBusinessUnitsCount > 1;
    }

    public get isSingleUserAssignee(): boolean {
        return this.assignedUserCount === 1;
    }

    public get isRoleUnitAssignees(): boolean {
        return this.assignmentType === FiTaskAssignmentType.RolesUnits;
    }

    public getUserAssigneesText(): string {
        if (this.isSingleUserAssignee) {
            return this.assignedUsers[0].FullName;
        }
        return `${this.assignedUsers.length} ${this.appTextService.get('common.staff_members').toLowerCase()}`;
    }

    public getStaffMemberLabel(): string {
        return `${this.assignedUserCount || 0} ` + this.appTextService.get('common.staff_members').toLowerCase();
    }

    public getAssignedRoleUnitText(): string {
        let result: string[] = [];
        if (this.assignedRolesCount > 0) {
            const rolesLabel = this.appTextService
                .get(getObjectTypeNameTextId(ObjectTypes.Role, this.assignedRolesCount > 1))
                .toLocaleLowerCase();
            result.push(`${this.assignedRolesCount} ${rolesLabel}`);
        }
        if (this.assignedBusinessUnitsCount > 0) {
            const businessUnitsLabel = this.appTextService
                .get(getObjectTypeNameTextId(ObjectTypes.BusinessUnit, this.assignedBusinessUnitsCount > 1))
                .toLocaleLowerCase();
            result.push(`${this.assignedBusinessUnitsCount} ${businessUnitsLabel}`);
        }
        if (!result.length) {
            return '-';
        }
        return result.join(', ');
    }

    public getAssigneesCountLabelByObjectType(objectType: ObjectTypes.Role | ObjectTypes.BusinessUnit): string {
        const count: number =
            objectType === ObjectTypes.Role ? this.assignedRolesCount : this.assignedBusinessUnitsCount;
        return `${count} ${this.getAssigneesLabelByObjectType(objectType, count > 1)}`;
    }

    public getAssigneesLabelByObjectType(
        objectType: ObjectTypes.Role | ObjectTypes.BusinessUnit,
        isPlural = true,
    ): string {
        return this.appTextService.get(getObjectTypeNameTextId(objectType, isPlural));
    }
}
